import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  obterChecklist,
  verificarPendente,
  concluirEtapa,
  pularEtapa,
  reiniciarOnboarding,
} from '../../../services/onboardingService';

// ============================================================
// Slice do wizard de onboarding (T1 / S6).
// Estado:
//   - checklist: { etapas, qtd_total, qtd_concluidas, concluido }
//   - pendente:  bool | null  (null = nao verificado ainda)
//   - etapaAtual: string da etapa em foco no wizard
// ============================================================

const initialState = {
  checklist: null,
  pendente: null,
  etapaAtual: null,
  loading: false,
  error: null,
};

export const fetchChecklist = createAsyncThunk(
  'onboarding/fetchChecklist',
  async () => obterChecklist(),
);

export const checarPendente = createAsyncThunk(
  'onboarding/checarPendente',
  async () => verificarPendente(),
);

export const concluirEtapaThunk = createAsyncThunk(
  'onboarding/concluirEtapa',
  async ({ etapa, dados }) => {
    const checklist = await concluirEtapa(etapa, dados);
    return checklist;
  },
);

export const pularEtapaThunk = createAsyncThunk(
  'onboarding/pularEtapa',
  async (etapa) => {
    const checklist = await pularEtapa(etapa);
    return checklist;
  },
);

export const reiniciarThunk = createAsyncThunk(
  'onboarding/reiniciar',
  async () => reiniciarOnboarding(),
);

const OnboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setEtapaAtual: (state, action) => {
      state.etapaAtual = action.payload;
    },
    limparErro: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChecklist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChecklist.fulfilled, (state, action) => {
        state.loading = false;
        state.checklist = action.payload;
        // Define a primeira etapa nao concluida/pulada como etapaAtual.
        if (!state.etapaAtual && action.payload?.etapas) {
          const proxima = action.payload.etapas.find(
            (e) => !e.concluida && !e.pulada,
          );
          if (proxima) state.etapaAtual = proxima.etapa;
        }
      })
      .addCase(fetchChecklist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Falha ao carregar onboarding';
      })

      .addCase(checarPendente.fulfilled, (state, action) => {
        state.pendente = action.payload?.pendente ?? false;
      })

      .addCase(concluirEtapaThunk.fulfilled, (state, action) => {
        state.checklist = action.payload;
        // Avanca para proxima etapa nao concluida.
        const proxima = action.payload?.etapas?.find(
          (e) => !e.concluida && !e.pulada,
        );
        state.etapaAtual = proxima ? proxima.etapa : null;
      })

      .addCase(pularEtapaThunk.fulfilled, (state, action) => {
        state.checklist = action.payload;
        const proxima = action.payload?.etapas?.find(
          (e) => !e.concluida && !e.pulada,
        );
        state.etapaAtual = proxima ? proxima.etapa : null;
      })

      .addCase(reiniciarThunk.fulfilled, (state, action) => {
        state.checklist = action.payload;
        state.etapaAtual = action.payload?.etapas?.[0]?.etapa || null;
        state.pendente = true;
      });
  },
});

export const { setEtapaAtual, limparErro } = OnboardingSlice.actions;
export default OnboardingSlice.reducer;
